import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X } from '@phosphor-icons/react';

const COMMON_CERTS = [
  "ICAO Annex 13 Investigation", "Aircraft Accident Investigation (AAI)", "ISASI Member",
  "Human Factors in Aviation Safety", "Flight Data Recorder Analysis", "Cockpit Voice Recorder Analysis",
  "Aircraft Structures & Metallurgy", "Aircraft Powerplant Investigation", "Marine Casualty Investigation",
  "Safety Management Systems (SMS) Auditor", "Dangerous Goods Investigation"
];

const AVIATION_SPECIALISTS = [
  'Operations Specialist', 'Witness Specialist', 'Meteorology Specialist',
  'ATS & Airports Specialist', 'Survivability Specialist', 'Cabin Safety Specialist',
  'Medical/HF Specialist', 'Maintenance & Records Specialist', 'Flight Recorders Specialist',
  'Systems Specialist', 'Structures Specialist', 'Powerplants Specialist',
  'Site Survey Specialist', 'Photo/Video Specialist'
];

const MARITIME_SPECIALISTS = [
  'Operations Specialist (Maritime)', 'Witness Specialist', 'Weather & Sea Conditions Specialist',
  'Port & Waterway Specialist', 'Survivability Specialist', 'Vessel Safety Specialist',
  'Human Factors Specialist', 'Maintenance & Records Specialist', 'Navigation Equipment Specialist',
  'Systems Specialist (Ship Systems)', 'Hull & Structures Specialist', 'Propulsion & Machinery Specialist',
  'Accident Scene Survey Specialist', 'Photo/Video Documentation Specialist'
];

function CertificationsField({ form, setForm, certInput, setCertInput }) {
  const addCert = (cert) => {
    const trimmed = cert.trim();
    if (trimmed && !(form.certifications || []).includes(trimmed)) {
      setForm({ ...form, certifications: [...(form.certifications || []), trimmed] });
    }
    setCertInput('');
  };
  const removeCert = (cert) => {
    setForm({ ...form, certifications: (form.certifications || []).filter(c => c !== cert) });
  };

  return (
    <div className="border-t pt-4">
      <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Certifications</Label>
      <div className="relative">
        <Input value={certInput} onChange={(e) => setCertInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCert(certInput); } }}
          placeholder="Type certification name..." className="rounded-lg" />
        {certInput.length >= 2 && (() => {
          const sugg = COMMON_CERTS.filter(c => c.toLowerCase().includes(certInput.toLowerCase()) && !(form.certifications || []).includes(c));
          return sugg.length > 0 ? (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
              {sugg.map((s) => (
                <div key={s} className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm" onClick={() => addCert(s)}>{s}</div>
              ))}
            </div>
          ) : null;
        })()}
      </div>
      {(form.certifications || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {form.certifications.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {c}
              <button type="button" onClick={() => removeCert(c)} className="hover:text-red-600"><X size={10} weight="bold" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1 mt-2">
        {COMMON_CERTS.filter(c => !(form.certifications || []).includes(c)).slice(0, 5).map((c) => (
          <button key={c} type="button" onClick={() => addCert(c)}
            className="px-2 py-0.5 text-[10px] rounded-full border border-slate-200 text-slate-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all">+ {c}</button>
        ))}
      </div>
    </div>
  );
}

function SpecializationsField({ form, setForm }) {
  const specialists = form.sector === 'maritime' ? MARITIME_SPECIALISTS : AVIATION_SPECIALISTS;
  const toggle = (spec) => {
    const current = form.specializations || [];
    if (current.includes(spec)) setForm({ ...form, specializations: current.filter(s => s !== spec) });
    else setForm({ ...form, specializations: [...current, spec] });
  };

  if (form.role !== 'specialist' && form.role !== 'investigator') return null;

  return (
    <div>
      <Label className="mb-2 block">
        {form.sector === 'aviation' ? 'Aviation' : form.sector === 'maritime' ? 'Maritime' : 'Aviation/Maritime'} Specializations
      </Label>
      <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-4 border-2 border-slate-300 rounded-lg bg-white">
        {specialists.map((spec) => (
          <label key={spec} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors">
            <input type="checkbox" checked={(form.specializations || []).includes(spec)}
              onChange={() => toggle(spec)} className="mt-0.5 rounded border-slate-300" />
            <span className="leading-tight">{spec}</span>
          </label>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-2">Selected: {(form.specializations || []).length} categories</p>
    </div>
  );
}

export function CreateUserDialog({ open, onOpenChange, form, setForm, certInput, setCertInput, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>Create New User</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-5">
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4">
            <p className="text-sm text-blue-900 font-medium">Creating user for: {form.sector === 'aviation' ? 'Aviation' : 'Maritime'} Investigations</p>
          </div>
          <div>
            <Label>Investigation Sector *</Label>
            <Select value={form.sector} onValueChange={(val) => setForm({ ...form, sector: val, specializations: [] })}>
              <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aviation">Aviation Investigations</SelectItem>
                <SelectItem value="maritime">Maritime Investigations</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg" required /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg" required /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Password *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-lg" placeholder="Temporary: 10+ characters, letters and numbers" required /></div>
            <div>
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={(val) => setForm({ ...form, role: val })}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="iic">Investigator-in-Charge (IIC)</SelectItem>
                  <SelectItem value="investigator">Investigator</SelectItem>
                  <SelectItem value="specialist">Specialist</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="border-t pt-4">
            <Label className="text-xs uppercase tracking-wider text-slate-500 mb-3 block">Professional Details</Label>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" className="rounded-lg" data-testid="create-phone" /></div>
              <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g., Senior Investigator" className="rounded-lg" data-testid="create-designation" /></div>
              <div><Label>Organization</Label><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="e.g., AAIB, NTSB, DGCA" className="rounded-lg" data-testid="create-organization" /></div>
              <div>
                <Label>Primary Specialty</Label>
                <Select value={form.primary_specialty} onValueChange={(val) => setForm({ ...form, primary_specialty: val })}>
                  <SelectTrigger className="rounded-lg" data-testid="create-specialty"><SelectValue placeholder="Select specialty" /></SelectTrigger>
                  <SelectContent>
                    {['Operations', 'Flight Recorders', 'Structures', 'Powerplants', 'Systems', 'Human Factors', 'Survivability', 'Cabin Safety', 'Maintenance', 'ATS & Airports', 'Meteorology', 'Witness Statements', 'Site Survey', 'Photo/Video', 'Maritime Operations', 'Vessel Structures', 'Maritime Propulsion', 'Navigation Systems'].map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Years of Experience</Label><Input type="number" min="0" max="50" value={form.years_of_experience} onChange={(e) => setForm({ ...form, years_of_experience: parseInt(e.target.value) || 0 })} className="rounded-lg" data-testid="create-experience" /></div>
              <div><Label>Investigations Completed</Label><Input type="number" min="0" value={form.investigations_completed} onChange={(e) => setForm({ ...form, investigations_completed: parseInt(e.target.value) || 0 })} className="rounded-lg" data-testid="create-inv-count" /></div>
            </div>
            <div className="mt-3"><Label>Qualifications</Label><Input value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} placeholder="e.g., MSc Aerospace, FAA A&P License" className="rounded-lg" data-testid="create-qualifications" /></div>
          </div>
          <CertificationsField form={form} setForm={setForm} certInput={certInput} setCertInput={setCertInput} />
          <SpecializationsField form={form} setForm={setForm} />
          <div className="flex gap-3 pt-4">
            <Button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg">Create User</Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditUserDialog({ open, onOpenChange, editUser, form, setForm, certInput, setCertInput, onSubmit }) {
  if (!editUser) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>Edit User: {editUser.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Investigation Sector</Label>
            <Select value={form.sector} onValueChange={(val) => setForm({ ...form, sector: val, specializations: [] })}>
              <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aviation">Aviation</SelectItem>
                <SelectItem value="maritime">Maritime</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(val) => setForm({ ...form, role: val })}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="iic">Investigator-in-Charge (IIC)</SelectItem>
                  <SelectItem value="investigator">Investigator</SelectItem>
                  <SelectItem value="specialist">Specialist</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val })}>
              <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="border-t pt-4">
            <Label className="text-xs uppercase tracking-wider text-slate-500 mb-3 block">Professional Details</Label>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="rounded-lg" /></div>
              <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="Designation" className="rounded-lg" /></div>
              <div><Label>Organization</Label><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="Organization" className="rounded-lg" /></div>
              <div><Label>Primary Specialty</Label><Input value={form.primary_specialty} onChange={(e) => setForm({ ...form, primary_specialty: e.target.value })} placeholder="Primary specialty" className="rounded-lg" /></div>
              <div><Label>Years of Experience</Label><Input type="number" min="0" value={form.years_of_experience} onChange={(e) => setForm({ ...form, years_of_experience: parseInt(e.target.value) || 0 })} className="rounded-lg" /></div>
              <div><Label>Investigations Completed</Label><Input type="number" min="0" value={form.investigations_completed} onChange={(e) => setForm({ ...form, investigations_completed: parseInt(e.target.value) || 0 })} className="rounded-lg" /></div>
            </div>
            <div className="mt-3"><Label>Qualifications</Label><Input value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} placeholder="Qualifications" className="rounded-lg" /></div>
          </div>
          <CertificationsField form={form} setForm={setForm} certInput={certInput} setCertInput={setCertInput} />
          <SpecializationsField form={form} setForm={setForm} />
          <div className="flex gap-3 pt-4">
            <Button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg">Save Changes</Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
